import gql from 'graphql-tag';
import cloneDeep from 'lodash/cloneDeep';
import 'moment-timezone';
import { compose, graphql } from 'react-apollo';
import fragments from 'utils/graphql/fragments';
import { formatOnlineOffers, formatPlsRewards } from './formatting';

const PLS_REWARDS_QUERY = gql`
  query {
    rewardsWithTimeZoneV2 {
      onlineOffers {
        ...OnlineOfferFields
      }
      plsPrizeAssignments {
        timeZoneHelpText
        prizeAssignments {
          playerId
          prizeAssignmentId
          originatingOnlinePrize {
            id
            name
            description
            category
            url
          }
          plsPrize {
            prizeId
            name
            description
            allowsPartialRedemption
            category
            prizeType
            amount
            value
            valueInCents
            property
          }
          assignmentState
          remainingValue
          remainingValueInCents
          usedValue
          usedValueInCents
          validFromDateTime
          validToDateTime
          dailyValidFromTime
          dailyValidToTime
          validFromDateTimeUTC
          validToDateTimeUTC
          dailyValidFromTimeUTC
          dailyValidToTimeUTC
          validDaysOfWeek
          blackoutDates
          assignmentEvents {
            id
            at
            eventName
            stateBeforeEvent
            stateAfterEvent
            redemptionData {
              redeemedValue
              remainingValue
              redemptionSource
              redemptionProperty
            }
          }
          isExpired
          isRedeemable
          requestId
        }
      }
    }
  }
  ${fragments.onlineOffers}
`;

const REDEEM_ONLINE_OFFER = gql`
  mutation redeemOnlineOffer($input: RedeemOnlineOfferInput!) {
    redeemOnlineOffer(input: $input) {
      offerUuid
      success
      message
    }
  }
`;

const RESET_NEW_OFFERS = gql`
  mutation resetNew {
    removeNewOffers
  }
`;

const SUBSCRIPTION_NEW_REWARDS = gql`
  subscription {
    newOffersV2 {
      onlineOffers {
        onlineOffers {
          name
          offerUuid
          patronAccountNumber
          cmUserId
          offerState
          startDate
          endDate
          redeemedDate
          startDateUTC
          endDateUTC
          redeemedDateUTC
          isNew
          offerType
          subCategory
          amount
          redeemedAmount
          description
          source
        }
        total
      }
      plsPrizeAssignments {
        timeZoneHelpText
        prizeAssignments {
          playerId
          prizeAssignmentId
          originatingOnlinePrize {
            id
            name
            description
            category
            url
          }
          plsPrize {
            prizeId
            name
            description
            allowsPartialRedemption
            category
            prizeType
            amount
            value
            valueInCents
            property
          }
          assignmentState
          remainingValue
          remainingValueInCents
          usedValue
          usedValueInCents
          validFromDateTime
          validToDateTime
          dailyValidFromTime
          dailyValidToTime
          validFromDateTimeUTC
          validToDateTimeUTC
          dailyValidFromTimeUTC
          dailyValidToTimeUTC
          validDaysOfWeek
          blackoutDates
          assignmentEvents {
            id
            at
            eventName
            stateBeforeEvent
            stateAfterEvent
            redemptionData {
              redeemedValue
              remainingValue
              redemptionSource
              redemptionProperty
            }
          }
          isExpired
          isRedeemable
          requestId
        }
      }
    }
  }
`;

export default compose(
  graphql(PLS_REWARDS_QUERY, {
    options: { fetchPolicy: 'cache-and-network' },
    props: ({ data, ownProps }) => {
      const rewardsWithTimeZone = data.rewardsWithTimeZoneV2;
      const plsRewardsData =
        rewardsWithTimeZone?.plsPrizeAssignments?.prizeAssignments ?? [];
      const onlineRewardsData =
        rewardsWithTimeZone?.onlineOffers?.onlineOffers ?? [];
      const plsRewards = formatPlsRewards(plsRewardsData);
      const onlineOffers = formatOnlineOffers(onlineRewardsData);

      return {
        plsRewardsData: data,
        plsRewards,
        onlineOffers,

        subscribeToNewPLSRewards: () => {
          return data.subscribeToMore({
            document: SUBSCRIPTION_NEW_REWARDS,
            updateQuery: (prev, { subscriptionData }) => {
              if (
                !subscriptionData.data ||
                !subscriptionData.data.newOffersV2 ||
                !subscriptionData.data.newOffersV2.plsPrizeAssignments ||
                !subscriptionData.data.newOffersV2.plsPrizeAssignments
                  .prizeAssignments ||
                !subscriptionData.data.newOffersV2.plsPrizeAssignments
                  .prizeAssignments.length
              ) {
                return null;
              }

              const reward =
                subscriptionData.data.plsPrizeAssignments.prizeAssignments[0];
              const rewards = [
                ...prev.rewardsWithTimeZoneV2.plsPrizeAssignments
                  .prizeAssignments
              ];
              rewards.push(reward);
              prev.rewardsWithTimeZoneV2.plsPrizeAssignments.prizeAssignments =
                rewards;
              return prev;
            }
          });
        }
      };
    }
  }),
  graphql(REDEEM_ONLINE_OFFER, {
    props: ({ ownProps, mutate }) => ({
      redeemOnlineOffer: (offerUuid, amount) =>
        mutate({
          variables: {
            input: { offerUuid, amount }
          },
          update: (cache, response) => {
            if (!response.data.redeemOnlineOffer.success) {
              return;
            }
            const data = cache.readQuery({
              query: PLS_REWARDS_QUERY
            });

            let rewards = cloneDeep(
              data.rewardsWithTimeZoneV2.onlineOffers.onlineOffers
            );

            let found = rewards.find(r => r.offerUuid === offerUuid);
            if (found) {
              found.redeemedAmount = amount;
            }

            data.rewardsWithTimeZoneV2.onlineOffers.onlineOffers = rewards;
            cache.writeQuery({
              query: PLS_REWARDS_QUERY,
              data
            });
          }
        })
    })
  }),
  graphql(RESET_NEW_OFFERS, {
    props: ({ ownProps, mutate }) => ({
      resetNewState: () =>
        mutate({
          update: (cache, response) => {
            const data = cache.readQuery({
              query: PLS_REWARDS_QUERY
            });

            let rewards = [
              ...data.rewardsWithTimeZoneV2.plsPrizeAssignments.prizeAssignments
            ];
            rewards.forEach(r => (r.isNewOffer = false));
            data.rewardsWithTimeZoneV2.plsPrizeAssignments.prizeAssignments =
              rewards;
            cache.writeQuery({
              query: PLS_REWARDS_QUERY,
              data
            });
          }
        })
    })
  })
);
